import { reportResult, RunDetail } from '../runs/common';
import { getConfig } from '../taskpane/configService';


// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

type MarginConfig = ReturnType<typeof getConfig>['run2_margins'];

interface SectionResult {
  sectionsChanged: number;
  changedSections: RunDetail[];
}


// ─────────────────────────────────────────────────────────────────────────
// Page-setup appliers  (each does one concern → zero nesting)
// ─────────────────────────────────────────────────────────────────────────

/** Applies the four simple margin edges. Returns true if anything changed. */
function applyEdgeMargins(ps: Word.PageSetup, m: MarginConfig): boolean {
  let changed = false;
  if (ps.topMargin    !== m.topMargin)    { ps.topMargin    = m.topMargin;    changed = true; }
  if (ps.bottomMargin !== m.bottomMargin) { ps.bottomMargin = m.bottomMargin; changed = true; }
  if (ps.leftMargin   !== m.leftMargin)   { ps.leftMargin   = m.leftMargin;   changed = true; }
  if (ps.rightMargin  !== m.rightMargin)  { ps.rightMargin  = m.rightMargin;  changed = true; }
  return changed;
}

/** Applies gutter position + size. Returns true if anything changed. */
function applyGutter(ps: Word.PageSetup, m: MarginConfig): boolean {
  if (ps.gutterPosition === m.gutterPosition && ps.gutter === m.gutter) return false;
  ps.gutterPosition = m.gutterPosition as any;
  ps.gutter         = m.gutter;
  return true;
}

/** Applies header/footer distances. Returns true if anything changed. */
function applyHeaderFooterDistance(ps: Word.PageSetup, m: MarginConfig): boolean {
  let changed = false;
  if (ps.headerDistance !== m.headerDistance) { ps.headerDistance = m.headerDistance; changed = true; }
  if (ps.footerDistance !== m.footerDistance) { ps.footerDistance = m.footerDistance; changed = true; }
  return changed;
}

/** Applies miscellaneous layout flags. Returns true if anything changed. */
function applyLayoutFlags(ps: Word.PageSetup, m: MarginConfig): boolean {
  let changed = false;
  if (ps.verticalAlignment          !== Word.VerticalAlignment.top) { ps.verticalAlignment          = Word.VerticalAlignment.top; changed = true; }
  if (ps.mirrorMargins              !== m.mirrorMargins)            { ps.mirrorMargins              = m.mirrorMargins;            changed = true; }
  if (ps.twoPagesOnOne              !== m.twoPagesOnOne)            { ps.twoPagesOnOne              = m.twoPagesOnOne;            changed = true; }
  if (ps.bookFoldPrinting           !== m.bookFoldPrinting)         { ps.bookFoldPrinting           = m.bookFoldPrinting;         changed = true; }
  if (ps.oddAndEvenPagesHeaderFooter !== m.oddAndEvenPagesHeaderFooter) {
    ps.oddAndEvenPagesHeaderFooter = m.oddAndEvenPagesHeaderFooter;
    changed = true;
  }
  return changed;
}

/**
 * Applies all margin/layout settings to one PageSetup object.
 * Returns true if any property was changed.
 */
function applyPageSetup(ps: Word.PageSetup, m: MarginConfig): boolean {
  const a = applyEdgeMargins(ps, m);
  const b = applyGutter(ps, m);
  const c = applyHeaderFooterDistance(ps, m);
  const d = applyLayoutFlags(ps, m);
  return a || b || c || d;
}


// ─────────────────────────────────────────────────────────────────────────
// Per-section processor
// ─────────────────────────────────────────────────────────────────────────

async function processSection(
  section: Word.Section,
  sectionIndex: number,
  context: Word.RequestContext,
  m: MarginConfig,
  result: SectionResult
): Promise<void> {
  const ps = section.pageSetup;

  ps.load([
    'topMargin', 'bottomMargin', 'leftMargin', 'rightMargin',
    'gutter', 'gutterPosition', 'verticalAlignment',
    'headerDistance', 'footerDistance', 'mirrorMargins',
    'twoPagesOnOne', 'bookFoldPrinting', 'oddAndEvenPagesHeaderFooter',
  ]);
  await context.sync();

  const changed = applyPageSetup(ps, m);
  if (!changed) return;

  result.sectionsChanged++;

  const firstPara = section.body.paragraphs.getFirst();
  firstPara.load('uniqueLocalId, text');
  await context.sync();

  const shortText = (firstPara.text || '').trim().substring(0, 80)
    || `(Section ${sectionIndex + 1} start)`;

  result.changedSections.push({
    paraId: firstPara.uniqueLocalId,
    label:  `Section ${sectionIndex + 1}: Page Margins corrected`,
    note:   shortText,
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────

export async function run2() {
  const cfg = getConfig();
  const m   = cfg.run2_margins;

  await Word.run(async (context) => {

    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();

    const sections = context.document.sections;
    sections.load('items');
    await context.sync();

    const result: SectionResult = { sectionsChanged: 0, changedSections: [] };

    for (let i = 0; i < sections.items.length; i++) {
      await processSection(sections.items[i], i, context, m, result);
    }

    await context.sync();

    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    await context.sync();

    const { sectionsChanged, changedSections } = result;

    console.log(`\u2713 Page margins corrected for ${sectionsChanged} section(s) (${cfg.clientName})`);

    if (sectionsChanged > 0) {
      reportResult('Page Margins Corrected', [
        { label: 'Sections updated', count: sectionsChanged },
        { label: 'Profile',          note: cfg.clientName },
        { label: 'Top',              note: m.topMargin + 'pt' },
        { label: 'Bottom/Left/Right', note: m.bottomMargin + 'pt' },
        ...changedSections,
      ]);
    } else {
      reportResult('Page Margins \u2014 no changes needed', [
        { label: 'All sections already have correct margins', note: cfg.clientName + ' standard' },
      ]);
    }
  });
}